import { removeHtmlTag } from '@exam-management/common/utils';
import { QBankStructureLevelCategory } from '@exam-management/qbs-provider';
import { z } from 'zod';

import type { CreateItemSetSubQuestionFormInput } from './types';

const MIN_P_VALUE = 0;
const MAX_P_VALUE = 1;

const DETAIL_DEFAULT_VALUES = {
  detail: {
    details: '<p></p>',
    choices: [
      { value: '1', label: '<p></p>', order: 1 },
      { value: '2', label: '<p></p>', order: 2 },
      { value: '3', label: '<p></p>', order: 3 },
      { value: '4', label: '<p></p>', order: 4 },
    ],
    correctAnswer: { value: undefined },
  },
};

const PROPERTIES_DEFAULT_VALUES = {
  properties: {
    learningObjectiveStatement: undefined,
    cognitiveDomain: [],
    solution: '<p></p>',
    reference: '<p></p>',
  },
};

const OWNERS_DEFAULT_VALUES = {
  owners: {
    owner: '',
    audit1: '',
    audit2: '',
    audit3: '',
  },
};

const LOCATION_DEFAULT_VALUES = {
  location: {},
};

const INFORMATION_DEFAULT_VALUES = {
  information: {
    calculated: '',
    pValue: {
      selected:
        'pInitial' as CreateItemSetSubQuestionFormInput['information']['pValue']['selected'],
      pInitial: '' as const,
      pStat: undefined,
    },
    bIndex: undefined,
  },
};

export const DEFAULT_FORM_VALUES = {
  ...LOCATION_DEFAULT_VALUES,
  ...INFORMATION_DEFAULT_VALUES,
  ...DETAIL_DEFAULT_VALUES,
  ...PROPERTIES_DEFAULT_VALUES,
  ...OWNERS_DEFAULT_VALUES,
};

const SearchSelectionSchema = z
  .object({
    value: z.string(),
    label: z.string(),
  })
  .nullish();

const ConvertEmptyStringToUndefinedSchema = z
  .string()
  .optional()
  .transform((value) => {
    if (value === '') return undefined;
    return value;
  });

export const CreateItemSetSubQuestionSchema = z.object({
  location: z.object({
    [QBankStructureLevelCategory.Part]: z
      .object({
        value: z.string(),
        label: z.string(),
      })
      .nullish(),
    [QBankStructureLevelCategory.Subject]: z
      .object({
        value: z.string(),
        label: z.string(),
      })
      .nullish(),
    [QBankStructureLevelCategory.Group]: z
      .object({
        value: z.string(),
        label: z.string(),
      })
      .nullish(),
    [QBankStructureLevelCategory.Chapter]: z
      .object({
        value: z.string(),
        label: z.string(),
      })
      .nullish(),
    [QBankStructureLevelCategory.Topic]: z
      .object({
        value: z.string(),
        label: z.string(),
      })
      .nullish(),
    [QBankStructureLevelCategory.SubTopic]: z
      .object({
        value: z.string(),
        label: z.string(),
      })
      .nullish(),
  }),
  information: z.object({
    calculated: z.string().optional(),
    pValue: z.object({
      selected: z.enum(['pInitial', 'pStat']),
      pInitial: z
        .union([
          z
            .number({
              message: '',
            })
            .min(MIN_P_VALUE, {
              message: `เกินค่าต่ำสุด ${MIN_P_VALUE}`,
            })
            .max(MAX_P_VALUE, {
              message: `เกินค่าสูงสุด ${MAX_P_VALUE}`,
            }),
          z.literal(''),
        ])
        .nullish()
        .transform((value) => {
          if (value === '') {
            return undefined;
          }
          return value;
        }),
      pStat: z.number().optional(),
    }),
    bIndex: z.preprocess((value) => {
      if (value === '') {
        return null;
      }
      return value;
    }, z.number().nullish()),
  }),
  detail: z.object({
    details: z.string().refine(
      (value) => {
        const details = removeHtmlTag(value, {
          customTag: 'p',
        }).trim();
        return details.length >= 1;
      },
      {
        message: 'กรุณากรอกรายละเอียดคำถาม',
      },
    ),
    choices: z
      .array(
        z.object({
          value: z.string(),
          label: z
            .string()
            .trim()
            .refine(
              (value) => {
                const label = removeHtmlTag(value, {
                  customTag: 'p',
                }).trim();
                return label.length >= 1;
              },
              {
                message: 'กรุณากรอกรายละเอียดคำตอบ',
              },
            ),
          order: z.number(),
        }),
      )
      .min(2, 'กรุณากรอกตัวเลือกอย่างน้อย 2 ข้อ'),
    correctAnswer: z.object({
      value: z.string({
        message: 'กรุณาเลือกตัวเลือกที่ถูกต้อง',
      }),
    }),
  }),
  properties: z.object({
    cognitiveDomain: z.array(SearchSelectionSchema).optional(),
    learningObjectiveStatement: SearchSelectionSchema,
    solution: z.string().optional(),
    reference: z.string().optional(),
  }),
  owners: z.object({
    owner: ConvertEmptyStringToUndefinedSchema,
    audit1: ConvertEmptyStringToUndefinedSchema,
    audit2: ConvertEmptyStringToUndefinedSchema,
    audit3: ConvertEmptyStringToUndefinedSchema,
  }),
});
